import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { marked } from 'marked';
import { SafeHtmlPipe } from '../../../pipes/safe-html.pipe';
import type {
  ChatMessage,
  ChatNavigation,
  ChatSlotFillSelectMode,
  ChatSuggestion,
} from '../chatbot.types';

export interface SlotChipToggleEvent {
  fieldKey: string;
  value: string;
  mode: ChatSlotFillSelectMode;
}

@Component({
  selector: 'ams-chatbot-message',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, SafeHtmlPipe],
  styleUrl: './chatbot-message.component.scss',
  templateUrl: './chatbot-message.component.html',
})
export class ChatbotMessageComponent {
  readonly message = input.required<ChatMessage>();
  readonly showCitations = input(false);
  // Slot-fill selections for THIS message only. Keyed by fieldKey.
  // Empty object when nothing is selected yet.
  readonly slotSelections = input<Record<string, string[]>>({});
  readonly sendSuggestion = output<ChatSuggestion>();
  readonly navigateTo = output<ChatNavigation>();
  readonly confirmAction = output<string>();
  readonly dismissAction = output<string>();
  readonly slotChipToggled = output<SlotChipToggleEvent>();

  readonly htmlContent = computed(() => {
    const currentMessage = this.message();
    if (currentMessage.role !== 'assistant' || currentMessage.isTyping) {
      return '';
    }

    return marked.parse(currentMessage.content.trim()) as string;
  });

  readonly navigationPrompt = computed(() => {
    return this.message().navigation?.find(n => !!n.prompt)?.prompt ?? null;
  });

  isChipSelected(fieldKey: string, value: string): boolean {
    return this.slotSelections()[fieldKey]?.includes(value) ?? false;
  }

  onSlotChipClick(fieldKey: string, value: string, mode: ChatSlotFillSelectMode): void {
    this.slotChipToggled.emit({ fieldKey, value, mode });
  }

  formatTime(date: Date): string {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  actionLabel(): string {
    return this.message().action?.label?.trim() || 'Open workflow';
  }
}
